import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiGrid, FiFileText, FiPlusCircle, FiLayers, FiLogOut, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const NAV = [
    { to: '/teacher/dashboard', icon: <FiGrid />, label: 'Dashboard' },
    { to: '/teacher/assignments', icon: <FiLayers />, label: 'My Assignments' },
    { to: '/teacher/papers', icon: <FiFileText />, label: 'My Papers' },
    { to: '/teacher/create-paper', icon: <FiPlusCircle />, label: 'Create Paper' },
    { to: '/teacher/profile', icon: <FiUser />, label: 'Profile' },
];

const BL = '#6366f1'; // brand indigo
const BG = '#eef2ff';

const TeacherSideNav = ({ collapsed, isMobile, onToggleCollapse, hasAssignments }) => {
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const handleLogout = () => { logout(); navigate('/login'); };

    const navItems = NAV.filter(item => {
        if (item.to === '/teacher/create-paper' && !hasAssignments) return false;
        return true;
    });

    if (isMobile) return (
        <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: 60, background: 'rgba(238,242,255,0.95)', backdropFilter: 'blur(16px)', borderTop: '1px solid rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-around', zIndex: 1000, paddingBottom: 'env(safe-area-inset-bottom, 0)' }}>
            {navItems.map(item => {
                // For mobile, shorten labels if needed or just use slightly smaller font
                let shortLabel = item.label;
                if (shortLabel === 'My Assignments') shortLabel = 'Assigns';
                if (shortLabel === 'My Papers') shortLabel = 'Papers';
                if (shortLabel === 'Create Paper') shortLabel = 'Create';

                return (
                    <NavLink key={item.to} to={item.to} style={({ isActive }) => ({ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textDecoration: 'none', color: isActive ? BL : '#94a3b8', fontSize: 10, fontWeight: isActive ? 700 : 500, fontFamily: "'Outfit', sans-serif", padding: '6px 8px', borderRadius: 12, background: isActive ? 'rgba(99,102,241,0.08)' : 'transparent', minWidth: 50 })}>
                        <span style={{ fontSize: 18 }}>{item.icon}</span>
                        <span style={{ fontSize: 9 }}>{shortLabel}</span>
                    </NavLink>
                );
            })}
        </nav>
    );

    const W = collapsed ? 72 : 250;
    return (
        <aside style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: W, background: 'linear-gradient(180deg,#e0e7ff 0%,#c7d2fe 50%,#eef2ff 100%)', display: 'flex', flexDirection: 'column', zIndex: 999, boxShadow: '4px 0 28px rgba(99,102,241,0.12)', transition: 'width 0.3s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', borderRight: '1px solid rgba(99,102,241,0.15)' }}>
            {/* Header */}
            <div style={{ padding: collapsed ? '18px 10px' : '18px 20px', borderBottom: '1px solid rgba(99,102,241,0.18)' }}>
                {collapsed ? (
                    <div onClick={onToggleCollapse} style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg,#818cf8,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', margin: '0 auto', boxShadow: '0 4px 14px rgba(99,102,241,0.28)' }}>
                        <span style={{ color: '#fff', fontSize: 20, fontWeight: 900, fontFamily: "'Outfit', sans-serif" }}>T</span>
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg,#818cf8,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 14px rgba(99,102,241,0.25)' }}>
                                <span style={{ color: '#fff', fontSize: 22, fontWeight: 900 }}>T</span>
                            </div>
                        </div>
                        <h2 style={{ fontSize: 13, fontWeight: 800, color: '#1e1b4b', fontFamily: "'Outfit', sans-serif", margin: 0, textAlign: 'center' }}>Swami Vivekananda<br />University</h2>
                        <p style={{ fontSize: 11, color: BL, margin: '5px 0 0', textAlign: 'center', fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>Teacher Portal</p>
                    </>
                )}
            </div>

            {/* Nav links */}
            <nav style={{ flex: 1, padding: collapsed ? '16px 8px' : '16px 12px', overflowY: 'auto' }}>
                {!collapsed && <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.8, color: 'rgba(30,27,75,0.38)', padding: '6px 12px 8px', fontFamily: "'Outfit', sans-serif" }}>Menu</div>}
                {navItems.map(item => (
                    <NavLink key={item.to} to={item.to} title={collapsed ? item.label : ''} style={({ isActive }) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '11px 0' : '11px 14px', justifyContent: collapsed ? 'center' : 'flex-start', borderRadius: 12, textDecoration: 'none', fontSize: 13, fontWeight: 600, marginBottom: 4, fontFamily: "'Outfit', sans-serif", color: isActive ? BL : 'rgba(30,27,75,0.65)', background: isActive ? 'rgba(99,102,241,0.14)' : 'transparent', boxShadow: isActive ? '0 2px 10px rgba(99,102,241,0.10)' : 'none' })}>
                        <span style={{ fontSize: collapsed ? 20 : 16, display: 'flex' }}>{item.icon}</span>
                        {!collapsed && <span>{item.label}</span>}
                        {!collapsed && <span style={{ marginLeft: 'auto', opacity: 0.3 }}>›</span>}
                    </NavLink>
                ))}
            </nav>

            {/* Logout */}
            <div style={{ padding: collapsed ? '10px 8px' : '10px 12px', borderTop: '1px solid rgba(99,102,241,0.15)' }}>
                <button onClick={handleLogout} title={collapsed ? 'Logout' : ''} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start', gap: 10, padding: collapsed ? '11px 0' : '11px 14px', borderRadius: 12, border: 'none', background: 'rgba(239,68,68,0.07)', color: '#dc6060', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>
                    <FiLogOut size={collapsed ? 20 : 16} />
                    {!collapsed && <span>Logout</span>}
                </button>
            </div>
        </aside>
    );
};

export default TeacherSideNav;
